import { TestRunService } from '@/api/testRun';
import { Empty } from '@/components/Empty';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { ChartShare } from '@/components/testRun/ChartShare';
import { ReleaseContent } from '@/pages/release/ReleaseContent';
import { useAssignedUserData } from '@/utils/hooks/useAssignedUserData';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { useChartData } from '@/utils/hooks/useChartData';
import { useStatusTestRunData } from '@/utils/hooks/useStatusTestRunData';
import { ChartData } from 'chart.js';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { DetailTestRun } from './DetailTestRun';
import { useProtectedRoute } from '@/routes/ProtectedRouteContext';

const renderChart = (
  data: Record<string, number> | undefined,
  chartData: ChartData<'pie'>,
  totalData: number,
  totalLabel: string,
  percentageLabel: string | undefined,
  labelColorPairs: { label: string; color: string }[],
) => {
  if (data && Object.keys(data).length > 0) {
    const percentages = labelColorPairs.map(({ label }, index) => {
      const value = chartData.datasets[0].data[index] ?? 0;
      const percentage = ((value / totalData) * 100).toFixed(0);
      return {
        user: label,
        percentage,
      };
    });

    return (
      <ChartShare
        isMultiPercentage
        chartData={chartData}
        total={totalData}
        percentage={percentages}
        totalLabel={totalLabel}
        percentageLabel={percentageLabel}
        labelColorPairs={labelColorPairs}
      />
    );
  } else {
    return <Empty />;
  }
};

export const ShareDetailTestRun: React.FC = () => {
  const { isPublic } = useProtectedRoute();
  const { testRunId } = useParams();
  const backNavigate = useBackNavigate();

  const { data: details } = TestRunService.useGet(testRunId);
  const { data, isFetching } = TestRunService.useGetShare(testRunId);

  const {
    statusChartData,
    totalStatus,
    labelColorPairs: statusLabelColorPairs,
  } = useStatusTestRunData(data?.status ?? {});

  const {
    chartData: issueChartData,
    totalData: totalIssueStatus,
    labelColorPairs: issueLabelColorPairs,
  } = useChartData(data?.issue_status ?? {}, ['#689536', '#509FE4']);

  const {
    assignedUserData,
    totalAssignedUsers,
    labelColorPairs: assignedUserLabelColorPairs,
  } = useAssignedUserData(data?.assigned_user ?? {});

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <div className="tab-group">
      <Section title={`Test Run - ${details?.title}`}>
        <div className="flex flex-col gap-6">
          <ReleaseContent title={'Test Run Result Status'}>
            <TabGroup className="tab-group">
              <TabList className="tab-list">
                <Tab className="tab-button">List</Tab>
                <Tab className="tab-button">Graph</Tab>
              </TabList>

              <Section>
                <TabPanels>
                  <TabPanel>
                    <DetailTestRun projectId={details?.project_id} />
                  </TabPanel>
                  <TabPanel>
                    {renderChart(
                      data?.status,
                      statusChartData,
                      totalStatus,
                      'Total Test Run',
                      'Passed',
                      statusLabelColorPairs,
                    )}
                  </TabPanel>
                </TabPanels>
              </Section>
            </TabGroup>
          </ReleaseContent>

          <ReleaseContent title={'Issue Status'}>
            {renderChart(
              data?.issue_status,
              issueChartData,
              totalIssueStatus,
              'Total Issues',
              'Closed',
              issueLabelColorPairs,
            )}
          </ReleaseContent>

          <ReleaseContent title={'Assigned User Contributions'}>
            {renderChart(
              data?.assigned_user,
              assignedUserData,
              totalAssignedUsers,
              'Total Contributions',
              undefined,
              assignedUserLabelColorPairs,
            )}
          </ReleaseContent>
        </div>
      </Section>

      {!isPublic && (
        <div className="tab-list">
          <OutlineButton onClick={backNavigate}>Back</OutlineButton>
        </div>
      )}
    </div>
  );
};
